import { Asset, ProjectType, BuildOption } from '@/lib/types'
import {
  Database,
  FileAudio,
  FileIcon,
  FileSpreadsheet,
  FileVideo2,
} from 'lucide-react'

export const initialPayload = {
  logoAssets: [] as Asset[],
  contentAssets: [] as Asset[],
  referenceDesignsAssets: [] as Asset[],
  selectedStyle: '' as string,
  customStyleDescription: '',
}

export const MAX_SUPER_PROMPT_ASSETS = 3

export const TABS = [
  'website',
  'store',
  'landingpage',
  'ai-app',
  'internal-tool',
  'prototype',
]

// Tab types
export type TabType = (typeof TABS)[number]

// Quick action configuration per tab
export const QUICK_ACTIONS: Record<TabType, string[]> = {
  website: ['Clone from URL', 'Company Homepage', 'Coach Website', 'Blog Site'],
  store: ['Sell eBooks / PDFs', 'Sell Digital Products', 'Sell Memberships'],
  landingpage: [
    'SEO Landing Page',
    'Ad Landing Page',
    'ABM Landing Page',
    'E-Commerce Landing Page',
  ],
  'ai-app': [
    'Chatbot App',
    'Image Generator',
    'Data Analysis Tool',
    'Recommendation Engine',
    'Voice Assistant',
  ],
  'internal-tool': [
    'Client Dashboard with Login',
    'Onboarding Portal',
    'Reporting Dashboard',
  ],
  prototype: [
    'Event Organizer Prototype',
    'Travel Planner Prototype',
    'Habit Tracker Prototype',
  ],
}

// Map tab types to ProjectType for database storage
export const TAB_TO_PROJECT_TYPE: Record<TabType, ProjectType> = {
  website: 'website',
  store: 'store',
  landingpage: 'landing_page',
  'ai-app': 'ai_app',
  'internal-tool': 'internal_tool',
}

// Get BuildOption based on selected tab
export const getTabBuildOption = (tab: TabType): BuildOption => {
  return tab === 'prototype' ? 'prototype' : 'complete'
}

// Homepage modes
export type HomepageMode = 'clone' | 'redesign'
export const HOMEPAGE_MODES: HomepageMode[] = ['clone', 'redesign']

export type IconComponent = typeof FileIcon
export const categoryIconMap: Record<string, IconComponent> = {
  video: FileVideo2,
  audio: FileAudio,
  csv: FileSpreadsheet,
  table: Database,
  super_prompt: Database, // Temporary, will handle separately
  pdf: FileIcon,
}

// Helper function to get appropriate icon for file type
export const getFileIcon = (
  assetType: string | undefined,
  fileName?: string,
) => {
  if (!assetType && !fileName) {
    return FileIcon
  }

  if (assetType && categoryIconMap[assetType]) {
    return categoryIconMap[assetType]
  }

  return FileIcon
}

// https://q2fqbsw0dz3.sg.larksuite.com/wiki/GsQuwf4yAiUGvOk7MuQlamkogyg
export const getTemplateText = (action: string) => {
  switch (action) {
    // Website
    case 'Clone from URL':
      return `Clone source URL: https://wimpykid.com/
 Keep style and layouts with this source url.
 Just replace the content and images with new ones called NextLaunch Studio.`

    case 'Company Homepage':
      return `Create a homepage for company: NovaEdge Technologies.
 Tagline: Empowering Businesses with AI and Automation.
 Services: AI Consulting, Automation Tools, Cloud Solutions.
 Email: contact@novaedge.com`

    case 'Coach Website':
      return `Create a website for coach: Samantha Reid.
 Focus: Mindset and Productivity Coaching.
 CTA: Book a Free Discovery Call.
 Email: hello@samanthacoach.com`

    case 'Blog Site':
      return `Create a blog named The Everyday Thinker by Daniel Lee.
 Topics: Productivity, Creativity, Life Lessons, Tools.
 Email: contact@everydaythinker.blog`

    // Store
    case 'Sell eBooks / PDFs':
      return `Create a store selling uploaded PDFs or eBooks.
 Store name: PageMint.
 Main product: "AI Business Guide" ($19).
 Email: support@pagemint.store`

    case 'Sell Digital Products':
      return `Build a shop for digital assets (UI Kits, Icons, Fonts).
 Brand: PixelSupply.
 Email: team@pixelsupply.io`

    case 'Sell Memberships':
      return `Create a membership site called CreatorPlus.
 Plans: Basic $9, Pro $29, Elite $59.
 Email: support@creatorplus.io`

    // Landing Page
    case 'SEO Landing Page':
      return `Create an SEO-focused page for brand SEOBoost.
 Goal: Capture leads and grow traffic.
 CTA: Subscribe for Free Tips.`

    case 'Ad Landing Page':
      return `Landing page for product AdMax Pro.
 Goal: Convert paid ad clicks into signups.
 CTA: Start Free Trial.`

    case 'ABM Landing Page':
      return `Create a B2B landing page for TargetPro ABM.
 Goal: Convert enterprise leads.
 CTA: Request a Demo.`

    case 'E-Commerce Landing Page':
      return `Product landing page for SmartPod Speaker ($99).
 CTA: Buy Now.
 Email: support@smartpod.com`

    // AI App - using basic templates for now
    case 'Chatbot App':
      return 'Create a chatbot application'

    case 'Image Generator':
      return 'Create an image generator application'

    case 'Data Analysis Tool':
      return 'Create a data analysis tool'

    case 'Recommendation Engine':
      return 'Create a recommendation engine'

    case 'Voice Assistant':
      return 'Create a voice assistant application'

    // Internal Tool
    case 'Client Dashboard with Login':
      return `Create a dashboard for clients to view projects and invoices.
 Tool name: ClientPortal.
 Email: support@clientportal.com`

    case 'Onboarding Portal':
      return `Create an onboarding portal for new users at WelcomeHub.
 Features: Steps, progress tracker, tutorials.`

    case 'Reporting Dashboard':
      return `Internal dashboard for performance analytics.
 Dashboard name: InsightView.
 Email: analytics@insightview.com`

    // Prototype
    case 'Event Organizer Prototype':
      return 'Prototype for EventMaster — manage events, attendees, and reminders.'

    case 'Travel Planner Prototype':
      return 'Prototype for TripMap — plan trips, book activities, share itineraries.'

    case 'Habit Tracker Prototype':
      return 'Prototype for DailyHabit — track habits, set reminders, view progress.'

    default:
      return `Build a ${action.toLowerCase()}`
  }
}
